import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, GraduationCap, Briefcase, Users, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

const AGE_GROUPS = [
  { id: "13-17", en: "13 – 17", hi: "13 – 17", focusEn: "Stream selection · Learning style · Career awareness", focusHi: "स्ट्रीम चयन · सीखने की शैली · करियर जागरूकता" },
  { id: "18-24", en: "18 – 24", hi: "18 – 24", focusEn: "Career selection · Higher education · Employability", focusHi: "करियर चयन · उच्च शिक्षा · रोज़गार क्षमता" },
  { id: "25-30", en: "25 – 30", hi: "25 – 30", focusEn: "Career growth · Skill gaps · Career transition", focusHi: "करियर विकास · कौशल अंतर · करियर परिवर्तन" },
  { id: "30+", en: "30+", hi: "30+", focusEn: "Leadership · Specialisation · Transition", focusHi: "नेतृत्व · विशेषज्ञता · परिवर्तन" },
];
const EDUCATION = [
  { id: "School (Class 8-10)", en: "School (Class 8–10)", hi: "स्कूल (कक्षा 8–10)" },
  { id: "Higher Secondary (11-12)", en: "Higher Secondary (11–12)", hi: "उच्च माध्यमिक (11–12)" },
  { id: "Undergraduate", en: "Undergraduate", hi: "स्नातक (चल रहा)" },
  { id: "Graduate", en: "Graduate", hi: "स्नातक पूर्ण" },
  { id: "Postgraduate+", en: "Postgraduate or higher", hi: "परास्नातक या उच्चतर" },
];
const STAGES = [
  { id: "Exploring careers", en: "Exploring careers", hi: "करियर खोज रहा/रही हूँ" },
  { id: "Preparing for exams", en: "Preparing for exams / admissions", hi: "परीक्षा / प्रवेश की तैयारी" },
  { id: "Looking for first job", en: "Looking for my first job", hi: "पहली नौकरी की तलाश" },
  { id: "Early career (0-3 yrs)", en: "Early career (0–3 years)", hi: "प्रारंभिक करियर (0–3 वर्ष)" },
  { id: "Considering a switch", en: "Considering a career switch", hi: "करियर बदलने पर विचार" },
];

function Choice({ selected, onClick, title, sub, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`w-full text-left rounded-lg border-2 px-4 py-3.5 transition-all flex items-start gap-3 ${
        selected ? "border-primary bg-primary/5 ring-2 ring-primary/15" : "border-border bg-white hover:border-primary/40"
      }`}>
      <span className={`mt-0.5 w-5 h-5 rounded-full grid place-items-center flex-shrink-0 border-2 ${
        selected ? "bg-primary border-primary text-primary-foreground" : "border-border"
      }`}>{selected && <Check className="w-3 h-3" />}</span>
      <span>
        <span className="block font-semibold text-sm">{title}</span>
        {sub && <span className="block text-xs text-muted-foreground mt-0.5">{sub}</span>}
      </span>
    </button>
  );
}

export default function Onboarding() {
  const { i18n } = useTranslation();
  const isHi = i18n.language?.startsWith("hi");
  const navigate = useNavigate();
  const [age, setAge] = useState(null);
  const [edu, setEdu] = useState(null);
  const [stage, setStage] = useState(null);
  const [busy, setBusy] = useState(false);
  const L = (o) => (isHi ? o.hi : o.en);

  const save = async () => {
    if (!age || !edu || !stage) { toast.error(isHi ? "कृपया तीनों विकल्प चुनें" : "Please answer all three"); return; }
    setBusy(true);
    try {
      await api.patch("/me/profile", { age_group: age, education_level: edu, career_stage: stage });
      navigate("/test");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="font-display font-extrabold">BharatIQ</span>
          <LanguageSwitcher compact />
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="onboarding-screen">
          <div className="label-eyebrow">{isHi ? "चरण 1 / 4 · प्रोफ़ाइल" : "Step 1 / 4 · Profile"}</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {isHi ? "अपनी रिपोर्ट को व्यक्तिगत बनाएँ।" : "Personalise your report."}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl text-sm sm:text-base">
            {isHi
              ? "आपकी उम्र, शिक्षा और करियर चरण के अनुसार सिफ़ारिशें और रोडमैप बदलते हैं। 30 सेकंड लगेंगे।"
              : "Recommendations, career matches and roadmaps change with your age, education and career stage. Takes 30 seconds."}
          </p>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <section>
            <div className="flex items-center gap-2 font-display font-bold"><Users className="w-4 h-4 text-primary" /> {isHi ? "आयु वर्ग" : "Age group"}</div>
            <div className="mt-4 space-y-2.5">
              {AGE_GROUPS.map((a) => (
                <Choice key={a.id} selected={age === a.id} onClick={() => setAge(a.id)} title={L(a)}
                  sub={isHi ? a.focusHi : a.focusEn} testId={`age-${a.id}`} />
              ))}
            </div>
          </section>
          <section>
            <div className="flex items-center gap-2 font-display font-bold"><GraduationCap className="w-4 h-4 text-primary" /> {isHi ? "शिक्षा स्तर" : "Education level"}</div>
            <div className="mt-4 space-y-2.5">
              {EDUCATION.map((e, i) => (
                <Choice key={e.id} selected={edu === e.id} onClick={() => setEdu(e.id)} title={L(e)} testId={`edu-${i}`} />
              ))}
            </div>
          </section>
          <section>
            <div className="flex items-center gap-2 font-display font-bold"><Briefcase className="w-4 h-4 text-primary" /> {isHi ? "करियर चरण" : "Career stage"}</div>
            <div className="mt-4 space-y-2.5">
              {STAGES.map((s, i) => (
                <Choice key={s.id} selected={stage === s.id} onClick={() => setStage(s.id)} title={L(s)} testId={`stage-${i}`} />
              ))}
            </div>
          </section>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground max-w-md">
            {isHi
              ? "BharatIQ एक शैक्षिक और करियर-मार्गदर्शन मंच है — यह कोई क्लिनिकल या चिकित्सीय निदान नहीं देता।"
              : "BharatIQ is an educational and career-guidance platform. It does not provide clinical or medical diagnosis."}
          </p>
          <button onClick={save} disabled={busy} data-testid="onboarding-continue-btn"
            className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-semibold disabled:opacity-60">
            {isHi ? "मूल्यांकन शुरू करें" : "Continue to assessment"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
